<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'toggle', 'restart', 'run-now']);

const { t } = useI18n();

const statusClasses = computed(() => {
  const classes = {
    pending: 'text-n-slate-11 bg-n-alpha-2',
    running: 'text-n-blue-11 bg-n-blue-3',
    paused: 'text-n-amber-11 bg-n-amber-3',
    completed: 'text-n-teal-11 bg-n-teal-3',
    failed: 'text-n-ruby-11 bg-n-ruby-3',
  };
  return classes[props.campaign.status] || 'text-n-slate-11 bg-n-alpha-2';
});

const statusLabel = computed(() => {
  const labels = {
    pending: t('WHATSAPP_BROADCAST.STATUS.PENDING'),
    running: t('WHATSAPP_BROADCAST.STATUS.RUNNING'),
    paused: t('WHATSAPP_BROADCAST.STATUS.PAUSED'),
    completed: t('WHATSAPP_BROADCAST.STATUS.COMPLETED'),
    failed: t('WHATSAPP_BROADCAST.STATUS.FAILED'),
  };
  return labels[props.campaign.status] || props.campaign.status;
});

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString();
};

const formatTime = time => {
  if (!time) return '-';
  return time;
};
</script>

<template>
  <div class="bg-n-solid-1 border border-n-weak rounded-lg p-4 hover:shadow-md transition-shadow">
    <div class="flex items-start justify-between gap-4">
      <!-- Image -->
      <div class="flex-shrink-0">
        <div
          v-if="campaign.image_url"
          class="w-16 h-16 rounded-lg overflow-hidden bg-n-alpha-2"
        >
          <img
            :src="campaign.image_url"
            :alt="campaign.title"
            class="w-full h-full object-cover"
          />
        </div>
        <div
          v-else
          class="w-16 h-16 rounded-lg bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-image text-2xl text-n-slate-9" />
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <h3 class="text-base font-semibold text-n-slate-12 truncate">
            {{ campaign.title }}
          </h3>
          <span
            class="text-xs font-medium inline-flex items-center h-5 px-2 py-0.5 rounded-md"
            :class="statusClasses"
          >
            {{ statusLabel }}
          </span>
        </div>

        <p class="text-sm text-n-slate-11 line-clamp-2 mb-2">
          {{ campaign.message }}
        </p>

        <div class="flex flex-wrap gap-4 text-xs text-n-slate-10">
          <div v-if="campaign.inbox" class="flex items-center gap-1">
            <span class="i-lucide-inbox" />
            <span>{{ campaign.inbox.name }}</span>
          </div>
          <div v-if="campaign.contact_list" class="flex items-center gap-1">
            <span class="i-lucide-users" />
            <span>{{ campaign.contact_list.name }} ({{ campaign.contact_list.contacts_count }})</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="i-lucide-calendar" />
            <span>{{ formatDate(campaign.start_date) }} - {{ formatDate(campaign.end_date) }}</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="i-lucide-clock" />
            <span>{{ formatTime(campaign.start_time) }} - {{ formatTime(campaign.end_time) }}</span>
          </div>
        </div>

        <!-- Stats -->
        <div class="flex gap-4 mt-3 text-xs">
          <div class="flex items-center gap-1">
            <span class="i-lucide-send text-green-500" />
            <span class="text-n-slate-11">{{ t('WHATSAPP_BROADCAST.STATS.SENT') }}:</span>
            <span class="font-medium text-n-slate-12">{{ campaign.sent_count }}</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="i-lucide-x-circle text-red-500" />
            <span class="text-n-slate-11">{{ t('WHATSAPP_BROADCAST.STATS.FAILED') }}:</span>
            <span class="font-medium text-n-slate-12">{{ campaign.failed_count }}</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="i-lucide-users text-blue-500" />
            <span class="text-n-slate-11">{{ t('WHATSAPP_BROADCAST.STATS.TOTAL') }}:</span>
            <span class="font-medium text-n-slate-12">{{ campaign.total_contacts }}</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2">
        <!-- Run Now button - only for pending campaigns -->
        <Button
          v-if="campaign.status === 'pending' && campaign.enabled"
          icon="i-lucide-play"
          size="small"
          variant="ghost"
          color-scheme="primary"
          :title="t('WHATSAPP_BROADCAST.ACTIONS.RUN_NOW')"
          @click="emit('run-now')"
        />
        <!-- Restart button - only for failed/completed campaigns -->
        <Button
          v-if="campaign.status === 'failed' || campaign.status === 'completed'"
          icon="i-lucide-refresh-cw"
          size="small"
          variant="ghost"
          color-scheme="primary"
          :title="t('WHATSAPP_BROADCAST.ACTIONS.RESTART')"
          @click="emit('restart')"
        />
        <Switch
          :model-value="campaign.enabled"
          @update:model-value="emit('toggle')"
        />
        <Button
          icon="i-lucide-pencil"
          size="small"
          variant="ghost"
          color-scheme="secondary"
          @click="emit('edit')"
        />
        <Button
          icon="i-lucide-trash-2"
          size="small"
          variant="ghost"
          color-scheme="ruby"
          @click="emit('delete')"
        />
      </div>
    </div>
  </div>
</template>
